<template>
	<header-block title="" border="0" bgcolor="transparent"></header-block>

	<div class="success-page">
		<h1 class="title">Policy added successfully</h1>

		<div class="hero-container">
			<div class="emoji-container" :class="{ 'animate': animateEmoji, 'fade-out': showPolicy }">
				<img src="../assets/tada.gif" alt="Celebration" class="party-emoji" />
			</div>

			<transition name="fade-slide">
				<div v-if="showPolicy && policy" class="policy-card-container">
					<AccountListItem
						:provider-name="policy.provider?.name || 'Provider'"
						:line1="policy.policyType?.name || 'Insurance'"
						:line2="policy.policyNumber || ''"
						:value="formatDate(policy.endDate)"
						label="Renewal"
						:avatar-url="policy.provider?.logoUri || require('@/assets/round1.png')"
					/>
				</div>
			</transition>
		</div>

		<div class="features-container">
			<transition name="fade-slide">
				<div v-if="showFeature1" class="feature-item">
					<img src="../assets/icon-document.svg" alt="Documents" class="feature-icon-img" />
					<div class="feature-text">
						Keep your documents safe,<br/>all in one place
					</div>
				</div>
			</transition>

			<transition name="fade-slide">
				<div v-if="showFeature2" class="feature-item">
					<img src="../assets/icon-calendar.svg" alt="Reminders" class="feature-icon-img" />
					<div class="feature-text">
						Get reminders before your<br/>policy is due to renew
					</div>
				</div>
			</transition>

			<transition name="fade-slide">
				<div v-if="showFeature3" class="feature-item">
					<img src="../assets/icon-chat.svg" alt="Chat" class="feature-icon-img" />
					<div class="feature-text">
						Chat with our assistant to<br/>understand your policy
					</div>
				</div>
			</transition>
		</div>

		<div class="anchor-bottom-no-nav">
			<MGButton text="Next" :arrow="true" :fullWidth="true" :disabled="!allAnimationsComplete" @click="handleNext"/>
		</div>
	</div>
</template>

<script>
import Services from '../mixins/Services';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import AccountListItem from './elements/AccountListItem.vue';

export default {
	mixins: [Services],
	components: { MGButton, HeaderBlock, AccountListItem },
	data() {
		return {
			animateEmoji: false,
			showPolicy: false,
			showFeature1: false,
			showFeature2: false,
			showFeature3: false,
			policy: null
		}
	},
	computed: {
		policyId() {
			return this.$route.query.policyId;
		},
		allAnimationsComplete() {
			return this.showFeature3;
		}
	},
	methods: {
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			const options = { day: 'numeric', month: 'long', year: 'numeric' };
			return date.toLocaleDateString('en-GB', options);
		},
		handleNext() {
			this.$router.push({ path: '/insurancemoredetails', query: { policyId: this.policyId } });
		},
		startAnimations() {
			// Start emoji animation immediately
			setTimeout(() => {
				this.animateEmoji = true;
			}, 100);

			// Show policy card over the gif after 2 seconds
			setTimeout(() => {
				this.showPolicy = true;
			}, 2000);

			// Stagger the feature items more slowly
			setTimeout(() => {
				this.showFeature1 = true;
			}, 2800);

			setTimeout(() => {
				this.showFeature2 = true;
			}, 3400);

			setTimeout(() => {
				this.showFeature3 = true;
			}, 4000);
		},
		onLoad: async function() {
			if (this.policyId) {
				this._getPolicy(this.policyId, (result) => {
					this.policy = result;
					this.startAnimations();
				});
			} else {
				// For testing without a real policy, use mock data
				this.policy = {
					provider: { name: 'AVIVA', logoUri: null },
					policyType: { name: 'Buildings and contents' },
					policyNumber: '',
					endDate: '2026-07-09'
				};
				this.startAnimations();
			}
		}
	},
	mounted: function () {
		this.onLoad();
	}
}
</script>

<style scoped>
.success-page {
	text-align: center;
	padding-bottom: 100px;
}

.title {
	font-size: 18px;
	font-weight: bold;
	margin-top: 30px;
	margin-bottom: 20px;
	color: var(--text-color);
}

.hero-container {
	position: relative;
	min-height: 120px;
	margin-bottom: 30px;
	display: flex;
	justify-content: center;
	align-items: center;
}

.emoji-container {
	display: flex;
	justify-content: center;
	align-items: center;
	opacity: 0;
	transform: scale(0.5);
	transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.emoji-container.animate {
	opacity: 1;
	transform: scale(1);
}

.emoji-container.fade-out {
	opacity: 0;
	transform: scale(0.8);
	transition: all 0.4s ease-out;
}

.party-emoji {
	width: 80px;
	height: 80px;
	display: block;
}

.policy-card-container {
	position: absolute;
	top: 50%;
	left: 0;
	right: 0;
	transform: translateY(-50%);
}

/* Hide arrow on the policy card */
.policy-card-container :deep(.arrow-icon) {
	display: none;
}

/* Fix text alignment on policy card */
.policy-card-container :deep(.account-list-item) {
	text-align: left;
}

.features-container {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 0 10px;
}

.feature-item {
	display: flex;
	align-items: center;
	gap: 16px;
	text-align: left;
}

.feature-icon-img {
	width: 40px;
	height: 40px;
	flex-shrink: 0;
}

.feature-text {
	font-size: 15px;
	line-height: 1.2;
	color: var(--text-color);
}

/* Transition animations */
.fade-slide-enter-active {
	transition: all 0.4s ease-out;
}

.fade-slide-leave-active {
	transition: all 0.3s ease-in;
}

.fade-slide-enter-from {
	opacity: 0;
	transform: translateY(20px);
}

.fade-slide-leave-to {
	opacity: 0;
	transform: translateY(-10px);
}
</style>
